/**
 * @file     sellerPrefill.ts
 * @readme   ./README.md
 * @purpose  The seller valuation's URL prefill contract: a page that knows the home (a listing's "Value my home") writes its unit, street, city, postal code, property type, beds and baths into /sell-house query params, and the seller flow reads them back as answers so it opens on the first step it still needs.
 */

import { getSellerPropertyTypeByCategory } from '@/core/config/sellerPropertyTypes';

export type SellerPrefill = {
  unit?: string | null;
  /** Street line only: number, direction, name and suffix. */
  street?: string | null;
  city?: string | null;
  postalCode?: string | null;
  /** A seller flow propertyType option: detached, condo, townhouse or other. */
  propertyType?: string | null;
  beds?: number | null;
  baths?: number | null;
};

const PARAM = {
  street: 'address',
  unit: 'unit',
  city: 'city',
  postalCode: 'postal',
  propertyType: 'type',
  beds: 'beds',
  baths: 'baths',
} as const;

const PROPERTY_TYPES = new Set(['detached', 'condo', 'townhouse', 'other']);
const MAX_BEDS = 5;
const MAX_BATHS = 4;
const MAX_LEN = 200;

/** The seller flow's type option for a dwelling category from dwellingCategory(). */
export function sellerPropertyTypeForCategory(category: string | null | undefined): string | null {
  if (!category) return null;
  return getSellerPropertyTypeByCategory(category)?.slug ?? 'other';
}

/** "5 or more" and "4 or more" are the top options, so counts clamp to them. */
function bedsOption(beds: number | null | undefined): string | null {
  if (beds == null || !Number.isFinite(beds) || beds < 0) return null;
  return String(Math.min(Math.floor(beds), MAX_BEDS));
}

function bathsOption(baths: number | null | undefined): string | null {
  if (baths == null || !Number.isFinite(baths) || baths < 1) return null;
  return String(Math.min(Math.floor(baths), MAX_BATHS));
}

/** /sell-house link that opens the valuation with this home's facts filled in. */
export function sellerValuationHref(prefill: SellerPrefill, base = '/sell-house'): string {
  const sp = new URLSearchParams();
  const set = (key: string, value: string | null | undefined) => {
    const v = value?.trim();
    if (v) sp.set(key, v);
  };
  set(PARAM.street, prefill.street);
  set(PARAM.unit, prefill.unit);
  set(PARAM.city, prefill.city);
  set(PARAM.postalCode, prefill.postalCode);
  set(PARAM.propertyType, prefill.propertyType && PROPERTY_TYPES.has(prefill.propertyType) ? prefill.propertyType : null);
  set(PARAM.beds, bedsOption(prefill.beds));
  set(PARAM.baths, bathsOption(prefill.baths));
  const query = sp.toString().replace(/\+/g, '%20');
  return `${base}${query ? `?${query}` : ''}#valuation-form`;
}

type ParamReader = { get: (key: string) => string | null };

/** Seller flow answers from the prefill params; unknown or malformed values are dropped. */
export function sellerPrefillAnswers(params: ParamReader): Record<string, string> {
  const read = (key: string) => params.get(key)?.trim().slice(0, MAX_LEN) || null;
  const answers: Record<string, string> = {};
  const street = read(PARAM.street);
  if (street) answers.address = street;
  const unit = read(PARAM.unit);
  if (unit) answers.unit = unit;
  const city = read(PARAM.city);
  if (city) answers.city = city;
  const postal = read(PARAM.postalCode);
  if (postal) answers.postal_code = postal.toUpperCase();
  const type = read(PARAM.propertyType);
  if (type && PROPERTY_TYPES.has(type)) answers.propertyType = type;
  const beds = read(PARAM.beds);
  if (beds && /^[0-5]$/.test(beds)) answers.beds = beds;
  const baths = read(PARAM.baths);
  if (baths && /^[1-4]$/.test(baths)) answers.baths = baths;
  return answers;
}
